import { loadMarketData } from './markets.js';
import { getMarket } from './market.js';

/**
 * Демо-режимдин адаптери: токен `DEMO_TOKEN` болсо axios серверге барбайт,
 * жоопту ушул жерден алат (`api/axios.js` тандайт).
 *
 * Маалымат учурдагы базардын файлынан (`data/<код>.js`) келет — ал
 * `scripts/demo-export.mjs` менен ЧЫНЫГЫ системадан чыгарылат. Бул жерде
 * эч нерсе ойлоп табылбайт: файлда жок нерсе бош же «—» болуп көрүнөт.
 * Кошумча иш — серверге окшош форма берүү гана.
 */
export const DEMO_TOKEN = 'demo-token';

const STAGES = ['translate', 'edit', 'proofread', 'verify'];

const LANGUAGE_NAMES = {
  en: 'Английский', ru: 'Русский', ky: 'Кыргызский', mn: 'Монгольский', kk: 'Казахский',
  uz: 'Узбекский', az: 'Азербайджанский', ka: 'Грузинский', hy: 'Армянский',
};

const page = (items) => ({
  items, meta: { total: items.length, page: 1, limit: Math.max(items.length, 50), totalPages: 1 },
});

/**
 * Этаптардын формасы эки башка: долбоордун главаларынын тизмеси аларды
 * ОБЪЕКТ катары окуйт (`c.stages.translate`), бир главанын бети — МАССИВ
 * катары (`stages.find(...)`). Чыныгы сервер да ошондой берет.
 */
const stageMap = (stages) => (Array.isArray(stages) ? Object.fromEntries(stages.map((s) => [s.stage, s])) : stages ?? {});
// Экспорттогу объекттин ичинде `stage` аты жок — ачкычтан кошулат, болбосо
// главанын бети ар этапты таба албай баарын «Не начато» көрсөтөт
const stageList = (stages) => (Array.isArray(stages)
  ? stages
  : Object.entries(stages ?? {}).map(([stage, s]) => ({ stage, ...s })));

/**
 * Долбоордун главалары.
 *
 * Чыныгы экспортто ар главанын өз абалы бар — ошол бойдон берилет. Эски
 * колдон жазылган маалыматта главалар аз же жок болушу мүмкүн, анда абалы
 * долбоордун `stageProgress`инен чыгарылат — тизмедеги пайыз менен
 * долбоордун бетиндеги конвейер дал келиши үчүн.
 */
function chaptersOf(project, written, chapterWord) {
  const mine = written.filter((c) => c.projectId === project.id);
  if (mine.length && mine[0].stages && !Array.isArray(mine[0].stages)) {
    return mine.map((c) => ({ ...c, stages: stageMap(c.stages) }));
  }

  const total = Math.max(project.chapterCount ?? 0, mine.length, 1);
  return Array.from({ length: total }, (_, i) => {
    const base = mine[i] ?? {
      id: `${project.id}-ch-${i + 1}`,
      projectId: project.id,
      title: `${chapterWord} ${i + 1}`,
      segmentCount: 0,
      isBookmarked: false,
    };
    const stages = {};
    for (const stage of STAGES) {
      const done = i < Math.round(((project.stageProgress?.[stage] ?? 0) / 100) * total);
      stages[stage] = { stage, status: done ? 'done' : 'not_started', progress: done ? 100 : 0 };
    }
    return { ...base, stages };
  });
}

/** Главаны id боюнча табат — жазылгандардан же курулгандардан */
function findChapter(id, data, chapterWord) {
  const direct = data.DEMO_CHAPTERS.find((c) => c.id === id);
  if (direct) return direct;
  const project = data.DEMO_PROJECTS.find((p) => id.startsWith(`${p.id}-ch-`));
  return project ? chaptersOf(project, data.DEMO_CHAPTERS, chapterWord).find((c) => c.id === id) : null;
}

/**
 * «Здоровье книги». Экспортто чыныгы отчёт болсо — ошол. Жок болсо этаптардын
 * даярдыгы главалардан эсептелет, ал эми сапаттын упайлары БЕРИЛБЕЙТ:
 * экранда «—» болот. Мурда бул жерде 99/100/98 деген ойдон чыгарылган сандар
 * турган — кардарга жасалма сапат көрсөткүчүн көрсөтүүгө болбойт.
 */
function healthOf(project, data, chapterWord) {
  const real = data.DEMO_HEALTH?.[project.id];
  if (real) return real;
  const chapters = chaptersOf(project, data.DEMO_CHAPTERS, chapterWord);
  const stages = {};
  for (const stage of STAGES) {
    const done = chapters.filter((c) => stageMap(c.stages)[stage]?.status === 'done').length;
    stages[stage] = { done, total: chapters.length, percent: chapters.length ? Math.round((done / chapters.length) * 100) : 0 };
  }
  return {
    stages,
    scores: { reconcile: null, terminology: null, names: null },
    counters: { needsAttention: 0, byType: { omission: 0, term: 0, fact: 0 } },
    lastCheck: null,
  };
}

/** Китеп боюнча издөө — демонун сегменттеринин ичинен, сервердикиндей форма менен */
function search(projectId, params, data) {
  const query = String(params?.q ?? '').trim();
  const needle = query.toLowerCase();
  const chapterIds = new Set(data.DEMO_CHAPTERS.filter((c) => c.projectId === projectId).map((c) => c.id));
  const titleOf = (id) => data.DEMO_CHAPTERS.find((c) => c.id === id)?.title ?? '';
  const items = [];
  for (const s of data.DEMO_SEGMENTS) {
    if (chapterIds.size && !chapterIds.has(s.chapterId)) continue;
    const inSource = s.sourceText?.toLowerCase().includes(needle);
    const translations = Object.entries(s.stages ?? {})
      .filter(([, v]) => v?.text?.toLowerCase().includes(needle))
      .map(([stage, v]) => ({ stage, text: v.text }));
    if (!needle || (!inSource && !translations.length)) continue;
    items.push({
      segmentId: s.id, chapterId: s.chapterId, chapterTitle: titleOf(s.chapterId),
      orderIndex: s.orderIndex ?? 0, page: 1, sourceText: s.sourceText, inSource, translations,
    });
  }
  return { query, total: items.length, truncated: false, items: items.slice(0, 100) };
}

function demoUser(market, data) {
  return {
    id: 'demo-user', login: 'demo', prefs: {},
    ...data.DEMO_USER,
    name: market.text.demoUser,
    role: 'demo',
    market: market.code,
  };
}

/** Сервердин маршруттарына окшош жооп */
async function route(path, config, market, data) {
  const [, root, id, sub] = path.split('/');
  const projects = data.DEMO_PROJECTS;
  const project = root === 'projects' && id ? projects.find((p) => p.id === id) ?? projects[0] : null;
  const word = market.text.chapter;

  if (path === '/auth/me' || path === '/auth/me/prefs') return demoUser(market, data);

  if (root === 'projects') {
    if (!id) return page(projects);
    if (!project) return null;
    if (!sub) return project;
    if (sub === 'chapters') return chaptersOf(project, data.DEMO_CHAPTERS, word);
    if (sub === 'glossary') {
      const all = data.DEMO_GLOSSARY ?? [];
      return all.some((t) => t.projectId) ? all.filter((t) => t.projectId === project.id) : all;
    }
    if (sub === 'health') return healthOf(project, data, word);
    if (sub === 'issues') return page([]);
    if (sub === 'exports') return [];
    if (sub === 'search') return search(project.id, config.params, data);
  }

  if (root === 'chapters' && id) {
    if (sub === 'segments') {
      const all = data.DEMO_SEGMENTS;
      // Эски колдон жазылган маалыматта сегменттин главасы көрсөтүлгөн эмес
      const mine = all.some((s) => s.chapterId) ? all.filter((s) => s.chapterId === id) : all;
      return page(mine);
    }
    const chapter = findChapter(id, data, word);
    return chapter ? { ...chapter, stages: stageList(chapter.stages) } : null;
  }

  // Сегменттин версиялары: экспортто ар этаптын учурдагы версиясы гана бар —
  // ошол берилет. Мурда бул жол жооп бербей, «История версий» панели кулачу
  if (root === 'segments' && sub === 'versions') {
    const segment = data.DEMO_SEGMENTS.find((s) => s.id === id);
    const version = segment?.stages?.[config.params?.stage];
    return { versions: version ? [{ ...version, isCurrent: true, model: { name: 'AI' } }] : [] };
  }

  if (root === 'jobs') return page([]);
  if (root === 'languages') {
    return [...new Set(['en', 'ru', market.lang])].map((code, i) => ({ id: i + 1, code, nameRu: LANGUAGE_NAMES[code] ?? code }));
  }

  // ⚠️ Бул үчөө керексиз көрүнгөнү менен МИЛДЕТТҮҮ: аларсыз демодо
  // «Настройки» бети ак экран болуп калат — AiModelsSection `items`ти
  // текшербей map кылат (мурда ушундан кулаган)
  if (root === 'ai-models') {
    return {
      providers: [{ name: 'anthropic', configured: true }, { name: 'openai', configured: true }],
      ocrModelId: 'm1',
      glossaryModelId: 'm1',
      items: [
        { id: 'm1', provider: 'anthropic', displayName: 'Claude (демо)', available: true, capabilities: { pdf: true }, isActive: true },
        { id: 'm2', provider: 'openai', displayName: 'GPT (демо)', available: true, capabilities: { pdf: false }, isActive: true },
      ],
    };
  }
  if (root === 'users') return page([demoUser(market, data)]);
  if (root === 'prompt-templates') return { items: [], variables: [] };
  if (root === 'backups') return id === 'alert' ? null : { settings: { enabled: false }, recent: [], lastSuccessAt: null };

  // Демодо өзгөртүү сурамдары (сактоо, иштетүү) эч нерсе кылбайт
  return { success: true };
}

export async function demoAdapter(config) {
  const market = getMarket();
  const data = await loadMarketData(market.code);
  const path = (config.url || '').replace(/^\/api/, '').split('?')[0];
  return {
    data: { success: true, data: await route(path, config, market, data) },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  };
}
